import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import DOMPurify from "dompurify";
import {
  ArrowRight,
  ArrowLeft,
  Play,
  X,
  Quote,
  Star,
  CheckCircle2,
  Gauge,
  MessageCircleHeart,
  FilePenLine,
  FileX2,
  CheckCircle,
  ShoppingCart,
  TrendingUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Seo, { makeBreadcrumb } from "@/components/seo/Seo";
import ImageLightbox from "@/components/ui/ImageLightbox";
import StickyLeadForm from "@/components/StickyLeadForm";
import { useSiteSettings } from "@/data/hooks";
import { Toaster } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// -----------------------------------------------------------------------------
// PresentationsFeaturePage - Bespoke version of the Feature Detail page for the
// Presentation Builder. Renders when the route matches /features/presentations
// and delivers the extra polish requested by the client:
//   1. A big hero with the laptop composite + a Watch Demo button + a scroll-
//      to-form CTA button.
//   2. A "Trusted by" logo strip below the hero.
//   3. A 2-column body/steps layout with a sticky "Request a Demo" form in
//      the right rail on desktop; mobile keeps the form at the bottom of the
//      page.
//   4. A Monica Scott / Logotology testimonial callout inserted mid-
//      walkthrough (after step 5) for social proof.
//   5. Watch Demo video modal wired to the same video_url used by the home
//      page Presentation Builder card (CMS-editable).
// -----------------------------------------------------------------------------

const TRUSTED_LOGOS = [
  { src: "/testimonial-logos/logotology.png", alt: "Logotology" },
  { src: "/testimonial-logos/beachley.png", alt: "Beachley Medical Office Solutions" },
  { src: "/testimonial-logos/spectrum-promo.png", alt: "Spectrum Promo" },
  { src: "/testimonial-logos/specialized.png", alt: "Specialized Office Systems" },
];

const HERO_BULLETS = [
  "Interactive branded presentations",
  "Customer comments & online approvals",
  "Real-time collaboration",
  "Save & reuse as templates",
];

const MONICA_TESTIMONIAL = {
  quote:
    "Before Advance, our daily operations felt less like 'management' and more like data archaeology - digging through multiple systems to track down clients and orders. Now, keeping all my orders in one place has essentially turned Advance into our operational command center. It took us from reactive firefighting to proactive planning. The biggest change? We actually spend our day acting on our orders rather than just trying to locate them.",
  author: "Monica Scott",
  company: "Owner, Logotology",
  logo: "/testimonial-logos/logotology.png",
};

// -- Watch Demo modal -------------------------------------------------------
function VideoModal({ open, onClose, url, poster }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="presentations-video-modal"
      className="fixed inset-0 z-[80] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={onClose}
          data-testid="presentations-video-close"
          className="absolute -top-11 right-0 text-white/80 hover:text-white flex items-center gap-1.5 text-sm font-medium"
        >
          Close <X className="h-4 w-4" />
        </button>
        <div className="rounded-2xl overflow-hidden bg-black">
          <video
            src={url}
            poster={poster || undefined}
            controls
            autoPlay
            playsInline
            className="w-full h-auto"
            data-testid="presentations-video-element"
          />
        </div>
      </div>
    </div>
  );
}

// -- Testimonial callout (mid-walkthrough) ---------------------------------
function TestimonialCallout() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.5 }}
      className="my-14 lg:my-20 rounded-2xl bg-white border border-slate-200 shadow-md px-6 py-8 sm:px-10 sm:py-10 relative overflow-hidden"
      data-testid="presentations-testimonial-callout"
    >
      <Quote className="absolute -top-2 -right-2 h-24 w-24 text-sky-50" />
      <div className="relative">
        <div className="flex items-center gap-3">
          <img
            src={MONICA_TESTIMONIAL.logo}
            alt={MONICA_TESTIMONIAL.company}
            className="h-10 w-auto object-contain"
            loading="lazy"
          />
          <div className="flex gap-0.5">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
            ))}
          </div>
        </div>
        <blockquote className="mt-5 text-base sm:text-lg text-slate-700 leading-relaxed italic">
          &ldquo;{MONICA_TESTIMONIAL.quote}&rdquo;
        </blockquote>
        <div className="mt-5 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center text-sky-600 text-sm font-bold">
            {MONICA_TESTIMONIAL.author.charAt(0)}
          </div>
          <div>
            <p className="text-sm font-bold text-slate-900">{MONICA_TESTIMONIAL.author}</p>
            <p className="text-xs text-slate-500">{MONICA_TESTIMONIAL.company}</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// -- Why Sales Teams Love section (client-provided mockup) ----------------
const SALES_BENEFITS = [
  {
    Icon: Gauge,
    title: "Faster proposal creation",
    desc: "Build professional, branded presentations in minutes.",
    iconBg: "bg-sky-100",
    iconColor: "text-sky-600",
    underline: "bg-sky-500",
  },
  {
    Icon: MessageCircleHeart,
    title: "Better customer engagement",
    desc: "Keep customers engaged with interactive presentations and real-time updates.",
    iconBg: "bg-emerald-100",
    iconColor: "text-emerald-600",
    underline: "bg-emerald-500",
  },
  {
    Icon: FilePenLine,
    title: "Fewer revisions",
    desc: "Customers can comment, request changes, and approve online — reducing back-and-forth.",
    iconBg: "bg-violet-100",
    iconColor: "text-violet-600",
    underline: "bg-violet-500",
  },
  {
    Icon: FileX2,
    title: "No duplicate data entry",
    desc: "Everything flows automatically — no rekeying and no data entry errors.",
    iconBg: "bg-amber-100",
    iconColor: "text-amber-600",
    underline: "bg-amber-500",
  },
  {
    Icon: CheckCircle,
    title: "Online approvals",
    desc: "Capture approvals and sign-offs directly within the presentation.",
    iconBg: "bg-teal-100",
    iconColor: "text-teal-600",
    underline: "bg-teal-500",
  },
  {
    Icon: ShoppingCart,
    title: "One-click quote and order creation",
    desc: "Convert selections into quotes or orders in one click and close faster.",
    iconBg: "bg-sky-100",
    iconColor: "text-sky-600",
    underline: "bg-sky-500",
  },
];

function WhySalesTeamsLove() {
  return (
    <section
      className="relative bg-gradient-to-b from-sky-50/40 via-white to-white py-16 sm:py-20 lg:py-24"
      data-testid="presentations-why-sales-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Heading + subtitle */}
        <motion.h2
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.55 }}
          className="text-center text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-slate-900 leading-[1.05]"
          data-testid="presentations-why-sales-heading"
        >
          Why Sales Teams Love{" "}
          <br className="hidden sm:block" />
          the Presentation Builder
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5, delay: 0.05 }}
          className="mt-4 text-center text-base sm:text-lg text-slate-500"
        >
          Save time. Engage customers. Close more business.
        </motion.p>

        {/* 6-card benefit grid */}
        <div
          className="mt-12 lg:mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-5 lg:gap-4"
          data-testid="presentations-why-sales-grid"
        >
          {SALES_BENEFITS.map((b, i) => (
            <motion.div
              key={b.title}
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.45, delay: 0.06 * i }}
              className="relative bg-white rounded-2xl ring-1 ring-slate-100 shadow-sm px-4 py-6 pb-8 flex flex-col items-center text-center overflow-hidden"
              data-testid={`presentations-why-sales-card-${i + 1}`}
            >
              {/* Icon bubble */}
              <span className={`h-16 w-16 rounded-full ${b.iconBg} flex items-center justify-center`}>
                <b.Icon className={`h-7 w-7 ${b.iconColor}`} strokeWidth={2} />
              </span>
              <h3 className="mt-5 text-base sm:text-[17px] font-extrabold text-slate-900 leading-tight">
                {b.title}
              </h3>
              <p className="mt-3 text-[13px] leading-relaxed text-slate-600 flex-1">
                {b.desc}
              </p>
              {/* Colored bottom underline */}
              <span className={`absolute left-6 right-6 bottom-3 h-1 rounded-full ${b.underline}`} aria-hidden />
            </motion.div>
          ))}
        </div>

        {/* Bottom callout banner */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-10 lg:mt-12 rounded-2xl bg-sky-50/80 ring-1 ring-sky-100 px-5 py-5 sm:px-8 sm:py-6 flex items-center justify-between gap-6"
          data-testid="presentations-why-sales-callout"
        >
          <div className="flex items-start sm:items-center gap-4 min-w-0">
            <span className="shrink-0 h-12 w-12 rounded-full bg-sky-600 flex items-center justify-center shadow-md shadow-sky-600/30">
              <TrendingUp className="h-6 w-6 text-white" strokeWidth={2.4} />
            </span>
            <div className="min-w-0">
              <p className="text-base sm:text-lg font-extrabold text-slate-900 leading-tight">
                Less admin. Happier customers. More closed deals.
              </p>
              <p className="mt-1 text-sm text-slate-600">
                The Presentation Builder helps your team sell smarter and win more.
              </p>
            </div>
          </div>
          {/* Small laptop illustration on the right (desktop only) */}
          <img
            src="/homepage-laptop.png"
            alt=""
            className="hidden md:block h-20 lg:h-24 w-auto object-contain shrink-0"
            aria-hidden
            loading="lazy"
          />
        </motion.div>
      </div>
    </section>
  );
}

// -- Main page --------------------------------------------------------------
export default function PresentationsFeaturePage() {
  const [feature, setFeature] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [videoOpen, setVideoOpen] = useState(false);
  const bodyRef = useRef(null);
  const formRef = useRef(null);
  const { settings } = useSiteSettings();

  // Prefer the CMS-editable video URL used by the homepage Presentation Builder
  // card so both surfaces stay in sync when the client swaps in a new clip.
  // Watch Demo video is hardcoded (not read from CMS) because the production
  // Mongo CMS override has repeatedly held a stale Academy URL.
  const videoUrl = "https://s3.us-east-1.amazonaws.com/images.anterasoftware.com/The+best+in+the+business.mp4";
  const slideImage = settings?.homepage?.presentation_feature?.slide_image || "/homepage-laptop.png";

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/features/presentations`);
        if (alive) setFeature(data);
      } catch {
        if (alive) setNotFound(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const cleanBody = feature ? DOMPurify.sanitize(feature.body_html || "") : "";

  // Attach click-to-zoom on body <img>s (mirrors FeatureDetailPage behaviour).
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return undefined;
    el.querySelectorAll("img").forEach((img) => {
      img.style.cursor = "zoom-in";
      img.setAttribute("data-zoomable", "true");
    });
    const onClick = (e) => {
      if (e.target?.tagName === "IMG" && e.target.getAttribute("src")) {
        e.preventDefault();
        setLightboxSrc(e.target.getAttribute("src"));
      }
    };
    el.addEventListener("click", onClick);
    return () => el.removeEventListener("click", onClick);
  }, [cleanBody]);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="presentations-loading">
        <p className="text-sm text-slate-400">Loading…</p>
      </div>
    );
  }
  if (notFound || !feature) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4" data-testid="presentations-not-found">
        <p className="text-lg font-semibold text-slate-900">This feature page isn&apos;t published yet.</p>
        <Link to="/features" className="mt-6">
          <Button className="bg-sky-600 hover:bg-sky-700 text-white rounded-full h-10 px-6 text-sm font-semibold">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Features
          </Button>
        </Link>
      </div>
    );
  }

  const steps = Array.isArray(feature.steps) ? feature.steps : [];
  // Split point for the testimonial callout - after step 5 if we have >= 6
  // steps; otherwise slot it in the middle.
  const midpoint = steps.length >= 6 ? 5 : Math.ceil(steps.length / 2);

  return (
    <div data-testid="presentations-feature-page" className="page-transition">
      <Seo
        title={feature.seo_title || `${feature.name} - Antera Software`}
        description={feature.seo_description || feature.short_desc}
        keywords={feature.seo_keywords}
        path="/features/presentations"
        jsonLd={makeBreadcrumb([
          { name: "Home", path: "/" },
          { name: "Features", path: "/features" },
          { name: feature.name, path: "/features/presentations" },
        ])}
      />
      <Toaster position="top-right" richColors />

      {/* ==================== HERO (full-width mockup image) ==================== */}
      <section className="pt-28 lg:pt-32 pb-10 lg:pb-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/features"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-sky-600 hover:text-sky-700"
            data-testid="presentations-back"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            All Features
          </Link>

          {/* Visually-hidden semantic H1 for SEO/a11y — the visible heading is
              baked into the mockup PNG below, so we surface it as text here so
              search engines and screen readers still see a proper H1. */}
          <h1
            className="sr-only"
            data-testid="presentations-h1"
          >
            The Industry&rsquo;s Best Presentation Builder
          </h1>

          {/* Full-width mockup image supplied by the client. Includes the
              PRESENTATIONS eyebrow, heading, description, 6-tile grid,
              Request a Demo visual button, trust text, and ACME laptop with
              all annotation callouts — all baked in.  We still expose a
              real "Watch Demo" + "Book a Demo" pair below so those flows
              remain clickable. */}
          <img
            src="/presentation-builder-showcase.png"
            alt="The Industry's Best Presentation Builder — interactive branded presentations, online approvals, customer comments, interactive pricing, branding, and one-click ordering."
            className="mt-6 w-full h-auto rounded-2xl shadow-md"
            data-testid="presentations-hero-image"
          />

          <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => setVideoOpen(true)}
              data-testid="presentations-watch-demo"
              className="btn-premium group inline-flex items-center gap-2.5 rounded-full bg-sky-600 hover:bg-sky-700 pl-2 pr-7 py-2 text-white shadow-lg shadow-sky-600/30 transition"
            >
              <span className="h-9 w-9 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center shrink-0">
                <Play className="h-4 w-4 text-white fill-white ml-0.5" />
              </span>
              <span className="text-sm font-bold tracking-wider uppercase">Watch Demo</span>
            </button>
            <button
              type="button"
              onClick={scrollToForm}
              data-testid="presentations-book-demo"
              className="inline-flex items-center gap-2 rounded-full border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white px-6 py-3 text-sm font-bold uppercase tracking-wider transition"
            >
              Book a Demo
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      {/* ==================== BODY + STEPS + STICKY FORM ==================== */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-10 lg:gap-14">
            {/* Left column - body + steps + mid-testimonial */}
            <div>
              {cleanBody && (
                <article
                  ref={bodyRef}
                  data-testid="presentations-body"
                  className="blog-article-content mb-12"
                  dangerouslySetInnerHTML={{ __html: cleanBody }}
                />
              )}

              {steps.length > 0 && (
                <>
                  {feature.steps_heading && (
                    <h2
                      className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900 mb-10 lg:mb-14"
                      data-testid="presentations-steps-heading"
                    >
                      {feature.steps_heading}
                    </h2>
                  )}
                  <div className="space-y-14 lg:space-y-20">
                    {steps.map((step, i) => (
                      <div key={i}>
                        <motion.div
                          initial={{ opacity: 0, y: 24 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true, margin: "-80px" }}
                          transition={{ duration: 0.5 }}
                          className={`flex flex-col ${i % 2 === 1 ? "lg:flex-row-reverse" : "lg:flex-row"} items-center gap-8 lg:gap-12`}
                          data-testid={`presentations-step-${i + 1}`}
                        >
                          <div className="w-full lg:flex-1 lg:min-w-0">
                            <h3 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
                              {step.title}
                            </h3>
                            {step.description && (
                              <p className="mt-4 text-[15px] sm:text-base text-slate-600 leading-relaxed whitespace-pre-line">
                                {step.description}
                              </p>
                            )}
                          </div>
                          <div className="w-full lg:flex-1 lg:min-w-0">
                            {step.image ? (
                              <button
                                type="button"
                                onClick={() => setLightboxSrc(step.image)}
                                className="rounded-2xl overflow-hidden border border-slate-200 shadow-md hover:shadow-xl bg-white transition-all block w-full group cursor-zoom-in"
                                aria-label={`View ${step.title} screenshot`}
                              >
                                <img
                                  src={step.image}
                                  alt={step.title}
                                  className="w-full h-auto object-cover group-hover:scale-[1.02] transition-transform duration-500"
                                  loading="lazy"
                                />
                              </button>
                            ) : (
                              <div className="rounded-2xl border border-dashed border-slate-300 bg-white/50 aspect-[16/10] flex items-center justify-center text-slate-400 text-sm">
                                No image yet
                              </div>
                            )}
                          </div>
                        </motion.div>

                        {/* Insert Monica testimonial callout after `midpoint`. */}
                        {i + 1 === midpoint && <TestimonialCallout />}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Right column - sticky demo form (desktop only) */}
            <aside className="hidden lg:block" ref={formRef}>
              <div className="sticky top-28">
                <StickyLeadForm
                  title="Presentation Builder"
                  variant="desktop"
                  headline="Book Your Demo"
                  subheadline="See the Presentation Builder in action"
                />
              </div>
            </aside>
          </div>

          {/* Mobile demo form - inline at the bottom */}
          <div className="lg:hidden mt-14" ref={formRef}>
            <StickyLeadForm
              title="Presentation Builder"
              variant="mobile"
              headline="Book Your Demo"
              subheadline="See the Presentation Builder in action"
            />
          </div>
        </div>
      </section>

      {/* ==================== WHY SALES TEAMS LOVE ==================== */}
      <WhySalesTeamsLove />

      {/* ==================== FINAL CTA ==================== */}
      <section className="bg-sky-600 py-14 lg:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white">
            See the Presentation Builder in your business.
          </h2>
          <p className="mt-3 text-sm text-sky-100">
            A personalized demo built around your workflows, your suppliers, and your business.
          </p>
          <button
            type="button"
            onClick={scrollToForm}
            className="inline-flex items-center mt-6 bg-white text-sky-700 hover:bg-sky-50 rounded-full px-8 h-12 text-sm font-semibold"
            data-testid="presentations-final-cta"
          >
            Request a Demo <ArrowRight className="ml-2 h-4 w-4" />
          </button>
        </div>
      </section>

      <VideoModal open={videoOpen} onClose={() => setVideoOpen(false)} url={videoUrl} />
      <ImageLightbox
        src={lightboxSrc}
        alt="Presentation Builder"
        onClose={() => setLightboxSrc(null)}
      />
    </div>
  );
}
